import { text } from '../app/i18n';
import type { Section } from './form-targets';

/**
 * Un élément du message « À compléter : … » (décision de l'utilisateur du 2026-10-19, comme l'écran v1) :
 * un champ (`field`, son libellé), une série sans ligne (`series`, son libellé) ou des lignes incomplètes
 * d'une série (`rows`, leur nombre). Même calcul que la tablette (`missingItems` de `form_paths.dart`),
 * jeu partagé `missing-toast.cases.json`.
 */
export interface MissingItem {
  kind: 'field' | 'series' | 'rows';
  label: string;
  count: number;
}

function fieldLabel(sections: readonly Section[], fieldId: string): string {
  for (const section of sections) {
    const entry = section.fields.find((candidate) => candidate.field.field_id === fieldId);
    if (entry !== undefined) return text(entry.field.label, 'fr', fieldId);
  }
  return fieldId;
}

/** Éléments du message « À compléter », dans l'ordre des manques, chacun une fois. */
export function missingItems(sections: readonly Section[], paths: readonly string[]): MissingItem[] {
  const order: string[] = [];
  const items = new Map<string, MissingItem>();
  const rows = new Map<string, Set<number>>();
  for (const path of paths) {
    const [, first, second] = path.split('.');
    if (first === undefined) continue;
    const grouped = sections.find((section) => section.section_id === first && (section.series !== undefined || section.repeat !== undefined));
    const index = grouped !== undefined && second !== undefined && /^\d+$/.test(second) ? Number(second) : undefined;
    const key = grouped === undefined ? `field:${first}` : index === undefined ? `series:${first}` : `rows:${first}`;
    if (index !== undefined) {
      const known = rows.get(first) ?? new Set<number>();
      known.add(index);
      rows.set(first, known);
    }
    if (items.has(key)) continue;
    order.push(key);
    items.set(key, grouped === undefined ? { kind: 'field', label: fieldLabel(sections, first), count: 0 } : { kind: index === undefined ? 'series' : 'rows', label: text(grouped.label, 'fr', first), count: 0 });
  }
  return order.map((key) => (key.startsWith('rows:') ? { ...items.get(key)!, count: rows.get(key.slice(5))!.size } : items.get(key)!));
}

const lower = (value: string): string => (value === '' ? value : `${value[0]!.toLowerCase()}${value.slice(1)}`);

/** Texte du message : « À compléter : chasseur · date de tir · 2 lignes à compléter ». */
export function missingText(items: readonly MissingItem[]): string {
  const parts = items.map((item) => (item.kind === 'rows' ? `${item.count} ${item.count > 1 ? 'lignes' : 'ligne'} à compléter` : item.kind === 'series' ? `${lower(item.label)} : aucune ligne` : lower(item.label)));
  return `À compléter : ${parts.join(' · ')}`;
}
